import React from "react";
import { Stack, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import ChatActionsMenu from "../ChatActionsMenu";
import {
  ChatInputContainer,
  ChatInputWrapper,
  StyledTextField,
  ActionsContainer,
  ActionButton,
  SendButton,
  TooltipContent,
  TooltipArrow,
  DisclaimerText,
  HiddenFileInput,
  FileCardsContainer,
  FileCard,
  FileCardContent,
  FileCardName,
  FileCardExtension,
  FileCardIcon,
  RemoveFileButton,
} from "./styled";
import { HistoryIconCustom, PaperclipIcon, SendIconCustom, FileDocumentIcon, CloseSmallIcon, FileOutlineIcon } from "Components/Generic/Icons/Dumont";

export interface ChatInputProps {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  isFocused: boolean;
  setFocused: (b: boolean) => void;
  isFullscreen?: boolean;
  onCopyConversation?: () => void;
  onExportChat?: () => void;
  onClearConversation?: () => void;
  onHistoryClick?: () => void;
  onFileUpload?: (content: string, fileName: string) => void;
  uploadedFileName?: string;
  onRemoveFile?: () => void;
  hasMessages?: boolean;
}

export default function ChatInput({
  value,
  onChange,
  onSend,
  onKeyPress,
  isFocused,
  setFocused,
  isFullscreen,
  onCopyConversation,
  onExportChat,
  onClearConversation,
  onHistoryClick,
  onFileUpload,
  uploadedFileName,
  onRemoveFile,
  hasMessages = false,
}: ChatInputProps) {
  const { t: translate } = useTranslation();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const ALLOWED_TYPES = [
      "text/plain",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];

    // Validar tamanho
    if (file.size > MAX_FILE_SIZE) {
      alert(translate("dumontFileTooLarge") || "File is too large. Maximum size is 10MB.");
      event.target.value = "";
      return;
    }

    // Validar tipo
    if (!ALLOWED_TYPES.includes(file.type)) {
      alert(translate("dumontUnsupportedFileType") || "Unsupported file type. Please select TXT, PDF, or DOC/DOCX files.");
      event.target.value = "";
      return;
    }

    try {
      if (file.type === "text/plain") {
        // Processar arquivo TXT localmente
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          if (onFileUpload) {
            onFileUpload(content, file.name);
          }
        };
        reader.readAsText(file);
      } else {
        // Para PDF e DOC, mostrar mensagem que será implementado no backend
        alert(translate("dumontDocumentProcessing") || "PDF/DOC processing will be available soon. Please use TXT files for now.");
      }
    } catch (error) {
      console.error("Error processing file:", error);
      alert(translate("dumontFileProcessingError") || "Error processing file. Please try again.");
    }

    // Reset input para permitir selecionar o mesmo arquivo novamente
    if (event.target) {
      event.target.value = "";
    }
  };

  return (
    <ChatInputContainer sx={{ maxWidth: isFullscreen ? "1200px" : "100%", margin: isFullscreen ? "0 auto" : "0" }}>
      {uploadedFileName && (
        <FileCardsContainer>
          <FileCard>
            <FileCardContent>
              <FileCardName title={uploadedFileName}>
                {uploadedFileName.replace(/\.[^/.]+$/, "")}
              </FileCardName>
              <FileCardExtension>
                <FileOutlineIcon sx={{ fontSize: 12, color: "inherit" }} />
                {uploadedFileName.split(".").pop()?.toUpperCase() || "FILE"}
              </FileCardExtension>
            </FileCardContent>
            <FileCardIcon>
              <FileDocumentIcon sx={{ fontSize: 36 }} />
            </FileCardIcon>
            <RemoveFileButton
              className="file-close-btn"
              onClick={onRemoveFile}
              size="small"
            >
              <CloseSmallIcon sx={{ fontSize: 10 }} />
            </RemoveFileButton>
          </FileCard>
        </FileCardsContainer>
      )}
      <ChatInputWrapper isFocused={isFocused} isFullscreen={isFullscreen}>
        <StyledTextField
          fullWidth
          placeholder={translate("dumontAskDumontAnything")}
          variant="outlined"
          multiline
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyPress={onKeyPress}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />

        <ActionsContainer>
          <Stack direction="row" spacing={1}>
            {hasMessages && (
              <ChatActionsMenu
                onCopyConversation={onCopyConversation}
                onExportChat={onExportChat}
                onClearConversation={onClearConversation}
              />
            )}
            <Tooltip
              title={translate("dumontHistory")}
              placement="top"
              arrow
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, 8],
                      },
                    },
                  ],
                },
                tooltip: {
                  sx: TooltipContent,
                },
                arrow: {
                  sx: TooltipArrow,
                },
              }}
            >
              <ActionButton size="small" onClick={onHistoryClick}>
                <HistoryIconCustom sx={{ fontSize: 16, color: "#666666" }} />
              </ActionButton>
            </Tooltip>
            <Tooltip
              title={translate("dumontUpload")}
              placement="top"
              arrow
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, 8],
                      },
                    },
                  ],
                },
                tooltip: {
                  sx: TooltipContent,
                },
                arrow: {
                  sx: TooltipArrow,
                },
              }}
            >
              <ActionButton size="small" onClick={handleFileUploadClick}>
                <PaperclipIcon sx={{ fontSize: 16, color: "#666666" }} />
              </ActionButton>
            </Tooltip>
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept=".txt,.pdf,.doc,.docx,text/plain,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleFileChange}
            />
          </Stack>

          <SendButton
            size="small"
            onClick={onSend}
            disabled={!value.trim()}
            isFocused={isFocused}
            hasValue={!!value.trim()}
          >
            <SendIconCustom
              sx={{
                fontSize: 16,
                color: isFocused && value.trim() ? "#FFFFFF" : "#666666",
                transition: "color 0.2s ease",
              }}
            />
          </SendButton>
        </ActionsContainer>
      </ChatInputWrapper>

      <DisclaimerText component="p">{translate("dumontUsesAI")}</DisclaimerText>
    </ChatInputContainer>
  );
}
